import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, ArrowUpRight, Library, Languages } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/ui/empty";
import { SkeletonRows } from "@/components/ui/skeleton";
import { api, type CardsListResp, type CardSummary } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 50;

// Langues filtrables : le code correspond au préfixe du card_id (EN_ASR_001 → EN),
// filtré côté API. Liste restreinte aux langues réellement présentes en base avec
// du volume (l'API accepte n'importe quel code ; on n'expose que les utiles).
const LANGS: ReadonlyArray<{ code: string; label: string; flag: string }> = [
  { code: "EN", label: "English", flag: "🇬🇧" },
  { code: "FR", label: "Français", flag: "🇫🇷" },
  { code: "DE", label: "Deutsch", flag: "🇩🇪" },
  { code: "IT", label: "Italiano", flag: "🇮🇹" },
  { code: "ES", label: "Español", flag: "🇪🇸" },
  { code: "JP", label: "日本語", flag: "🇯🇵" },
  { code: "KO", label: "한국어", flag: "🇰🇷" },
  { code: "CN", label: "中文", flag: "🇨🇳" },
  { code: "PT", label: "Português", flag: "🇵🇹" },
];

/**
 * Catalogue : la liste paginée complète des cartes (déplacée depuis l'accueil).
 * Accessible via l'entrée de menu « Catalogue ». Le filtre texte reste classique
 * (table filtrée), l'autocomplétion vit dans la recherche globale du TopNav.
 */
export function Cards() {
  const { t, fmtMoney } = useI18n();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [langs, setLangs] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CardsListResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const toggleLang = useCallback((code: string) => {
    setLangs((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const langParam = langs.length ? `&languages=${encodeURIComponent(langs.join(","))}` : "";
      const r = await api<CardsListResp>(
        `/api/cards?search=${encodeURIComponent(debounced)}${langParam}&limit=${PAGE_SIZE}&page=${page}`,
      );
      setData(r);
    } catch (e) {
      setError((e as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [debounced, langs, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Toute nouvelle recherche OU changement de filtre langue ramène en page 1.
  useEffect(() => {
    setPage(1);
  }, [debounced, langs]);

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
          <Library className="h-3.5 w-3.5" />
          {t("nav.catalog") || "Catalog"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("cards.title") || "Card catalog"}
        </h1>
        <p className="mt-1 max-w-xl text-sm text-muted-fg">
          {t("cards.subtitle") || "Browse the full indexed card base. Click any card to inspect prices and orderbook."}
        </p>
      </header>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>{t("home.search.heading") || "Browse cards"}</CardTitle>
            <CardDescription>
              {t("home.search.help") || "Filter by card ID (e.g. EN_ASR_001) or partial name."}
            </CardDescription>
          </div>
          <Badge tone="neutral">{total ? `${total.toLocaleString()} cards` : "—"}</Badge>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              leftIcon={<Search />}
              placeholder={t("home.search.placeholder") || "EN_ASR_001 or card name"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="max-w-md flex-1"
            />
            {loading ? <Badge tone="info">Loading…</Badge> : null}
            {error ? <Badge tone="danger">{error}</Badge> : null}
          </div>

          {/* Filtre langue : cases à cocher multi-sélection. Aucune cochée = toutes
              les langues (comportement par défaut). Filtré côté API sur le préfixe
              du card_id. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-1 inline-flex items-center gap-1 text-xs font-medium text-muted-fg">
              <Languages className="h-3.5 w-3.5" />
              {t("cards.filter.language") || "Language"}
            </span>
            {LANGS.map((l) => {
              const on = langs.includes(l.code);
              return (
                <label
                  key={l.code}
                  title={l.label}
                  className={cn(
                    "inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                    on
                      ? "border-primary/40 bg-primary/12 text-primary"
                      : "border-border bg-surface text-muted-fg hover:bg-surface-2",
                  )}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={on}
                    onChange={() => toggleLang(l.code)}
                  />
                  <span aria-hidden>{l.flag}</span>
                  <span className="font-medium">{l.code}</span>
                </label>
              );
            })}
            {langs.length ? (
              <button
                type="button"
                onClick={() => setLangs([])}
                className="ml-1 text-xs text-muted-fg underline-offset-2 hover:text-fg hover:underline"
              >
                {t("cards.filter.clear") || "Clear"}
              </button>
            ) : null}
          </div>

          <div className="overflow-hidden rounded-md border border-border">
            <Table>
              <THead>
                <TR>
                  <TH>Card ID</TH>
                  <TH>Name</TH>
                  <TH>Set</TH>
                  <TH>Rarity</TH>
                  <TH className="text-right">Avg price</TH>
                  <TH className="text-right">Asked</TH>
                  <TH className="text-right w-24">Action</TH>
                </TR>
              </THead>
              <TBody>
                {loading && items.length === 0 ? (
                  <SkeletonRows rows={6} cols={7} />
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <Empty
                        icon={<Search className="h-5 w-5" />}
                        title={t("home.empty.title") || "No cards found"}
                        description={t("home.empty.desc") || "Try a shorter query or different language code."}
                      />
                    </td>
                  </tr>
                ) : (
                  items.map((row) => <CardRow key={row.id} row={row} fmtMoney={fmtMoney} />)
                )}
              </TBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-xs text-muted-fg">
            <span>
              Page {data?.page ?? page} / {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function CardRow({ row, fmtMoney }: { row: CardSummary; fmtMoney: (v: number | string | null | undefined, c?: string) => string }) {
  const avg = row.price != null ? fmtMoney(row.price) : "—";
  const asked = row.asked_price != null ? fmtMoney(row.asked_price) : "—";
  return (
    <TR>
      <TD>
        <Link
          to={`/card/${encodeURIComponent(row.id)}`}
          className="font-mono text-xs text-primary hover:underline"
        >
          {row.id}
        </Link>
      </TD>
      <TD className="max-w-[260px] truncate font-medium">{row.name ?? "—"}</TD>
      <TD className="text-muted-fg">
        {row.set_name ?? row.set_code ?? "—"}
        {row.number ? <span className="ml-1 text-[10px] text-muted-fg/70">#{row.number}</span> : null}
      </TD>
      <TD>
        {row.rarity ? <Badge tone="info" variant="soft">{row.rarity}</Badge> : <span className="text-muted-fg">—</span>}
      </TD>
      <TD className="text-right font-mono">{avg}</TD>
      <TD className="text-right font-mono text-muted-fg">{asked}</TD>
      <TD className="text-right">
        <Link
          to={`/card/${encodeURIComponent(row.id)}`}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] font-medium text-muted-fg hover:bg-surface-2 hover:text-fg"
        >
          Inspect <ArrowUpRight className="h-3 w-3" />
        </Link>
      </TD>
    </TR>
  );
}
